import React, { useMemo } from 'react'
import { Box, Tooltip, IconButton } from '@mui/material'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import type { WaveCurve } from '@/interfaces/waveChart'
import { ChartThemeManager, type ChartTheme } from '../Services/ChartThemeManager'

export interface LegendItem {
  name: string
  color?: string
  yAxisId?: string
  visible?: boolean
  /** Unique id of the curve in format fileType:curveName if available */
  curveId?: string
}

export interface LegendCompactProps {
  items: LegendItem[]
  theme?: ChartTheme
  corner?: 'tl' | 'tr' | 'bl' | 'br'
  onItemClick?: (item: LegendItem) => void
  onToggleVisibility?: (item: LegendItem) => void
}

/**
 * Compact legend showing color swatches and curve names.
 * - header variant: inline chips row, non-interactive for now
 * - overlay variant: draggable floating panel inside the chart
 */
export const LegendCompact: React.FC<LegendCompactProps> = ({ items, theme = 'dark', corner = 'tr', onItemClick, onToggleVisibility }) => {
  const colors = ChartThemeManager.getChartColors(theme)
  const styles = ChartThemeManager.getChartStyles()

  // Show all items; indicate hidden via opacity rather than removing from legend
  const legendItems = useMemo(() => items.filter(i => i.name), [items])
  // overlay variant with fixed corners to avoid zoom/drag conflicts
  const cornerStyle: React.CSSProperties = useMemo(() => {
    const base: React.CSSProperties = { position: 'absolute', backgroundColor: 'rgba(0,0,0,0.4)', color: colors.text.primary, padding: '6px 8px', borderRadius: '6px', backdropFilter: 'blur(2px)', userSelect: 'none', maxWidth: '60%', zIndex: 2 }
    // Heuristic offsets to avoid overlapping axes labels/ticks – inset significantly
    const leftInset = 96 // nudge more to the right for left-side axes
    const bottomInset = 64 // higher for x-axis labels
    const rightInset = 24
    const topInset = 12
    switch (corner) {
      case 'tl': return { ...base, top: topInset, left: leftInset }
      case 'tr': return { ...base, top: topInset, right: rightInset }
      case 'bl': return { ...base, bottom: bottomInset, left: leftInset }
      case 'br': return { ...base, bottom: bottomInset, right: rightInset }
      default: return { ...base, top: topInset, right: rightInset }
    }
  }, [corner, colors.text.primary])

  return (
    <Box style={cornerStyle} data-testid="legend-compact-overlay">
      <Box sx={{ fontSize: styles.fontSize.small, marginBottom: 0.5, opacity: 0.8 }}>Legend</Box>
      <Box data-testid="legend-list" sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {legendItems.map((item, idx) => (
          <Tooltip key={`${item.name}-${idx}`} title={`${item.name}${item.yAxisId ? ` • ${item.yAxisId}` : ''}`}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Box
                role={onToggleVisibility ? 'button' : undefined}
                tabIndex={onToggleVisibility ? 0 : undefined}
                onClick={onToggleVisibility ? () => onToggleVisibility(item) : undefined}
                onKeyDown={onToggleVisibility ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggleVisibility(item) } } : undefined}
                sx={{ 
                  display: 'inline-flex', 
                  alignItems: 'center', 
                  gap: 0.75, 
                  cursor: onToggleVisibility ? 'pointer' : 'default'
                }}
              >
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 2,
                    backgroundColor: item.color || colors.text.primary,
                    display: 'inline-block'
                  }}
                />
                <span style={{ fontSize: styles.fontSize.small, whiteSpace: 'nowrap', opacity: item.visible === false ? 0.35 : 1 }}>{item.name}</span>
              </Box>

              {/* Config icon; stop propagation so it doesn't toggle visibility */}
              {onItemClick && (
                <IconButton
                  size="small"
                  aria-label={`Configure ${item.name}`}
                  onClick={(e) => { e.stopPropagation(); onItemClick(item) }}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onItemClick(item) } }}
                  sx={{ color: colors.text.primary, opacity: 0.9 }}
                >
                  <SettingsOutlinedIcon fontSize="inherit" />
                </IconButton>
              )}
            </Box>
          </Tooltip>
        ))}
      </Box>
    </Box>
  )
}

export function curvesToLegendItems(curves: WaveCurve[]): LegendItem[] {
  return curves.map(c => {
    const fileType = c.sourceFile
    let curveNameForId: string | undefined
    if (fileType) {
      // ChartDataResolver names curves as "<fileType>: <curveName>" - extract original curveName
      if (typeof c.name === 'string' && c.name.startsWith(`${fileType}: `)) {
        curveNameForId = c.name.substring(fileType.length + 2)
      } else {
        curveNameForId = c.name
      }
    }
    const curveId = fileType && curveNameForId ? `${fileType}:${curveNameForId}` : undefined
    // Display name for legend: prefer displayName when provided
    const display = c.displayName && c.displayName.trim().length > 0 ? c.displayName.trim() : c.name
    return { name: display, color: c.color, yAxisId: c.yAxisId, visible: c.visible !== false, curveId }
  })
}

export default LegendCompact
